import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { 
  ArrowLeft, 
  TrendingUp,
  Calendar,
  Sparkles,
  RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Rendimentos() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [rendimentos, setRendimentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalRendimentos, setTotalRendimentos] = useState(0);

  useEffect(() => {
    fetchRendimentos();
  }, []);

  const fetchRendimentos = async () => {
    try {
      const response = await axios.get(`${API_URL}/transactions`);
      // Filter profit/utility payments AND reinvestments (compound interest)
      const filtered = response.data.filter(t => 
        t.type === 'utility_payment' || t.type === 'profit' || t.type === 'reinvestment'
      );
      setRendimentos(filtered);
      
      // Calculate total
      const total = filtered.reduce((sum, t) => sum + t.amount, 0);
      setTotalRendimentos(total);
    } catch (error) {
      // Error silenced: 'Failed to fetch rendimentos:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value || 0);
  };

  return (
    <div className="animate-fade-in" data-testid="rendimentos-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-slate-400" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-white font-['Outfit']">{t('returns.title')}</h1>
          <p className="text-slate-400 text-sm">{t('returns.subtitle')}</p>
        </div>
      </div>

      {/* Total Card */}
      <div className="glass-card p-6 mb-6 glow-green">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 flex items-center justify-center">
            <Sparkles className="w-7 h-7 text-emerald-400" />
          </div>
          <div>
            <p className="text-slate-400 text-sm">{t('returns.totalReceived')}</p>
            <p className="text-3xl font-bold text-emerald-400 font-['Outfit']">
              {formatCurrency(totalRendimentos)}
            </p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
        </div>
      ) : rendimentos.length > 0 ? (
        <div className="glass-card overflow-hidden">
          <div className="divide-y divide-white/5">
            {rendimentos.map((rendimento) => {
              const isReinvestment = rendimento.type === 'reinvestment';
              const IconComponent = isReinvestment ? RefreshCw : TrendingUp;
              const iconBg = isReinvestment ? 'bg-purple-500/10' : 'bg-emerald-500/10';
              const iconColor = isReinvestment ? 'text-purple-400' : 'text-emerald-400';
              const amountColor = isReinvestment ? 'text-purple-400' : 'text-emerald-400';
              
              // Build translated description
              const getTranslatedDescription = () => {
                const desc = rendimento.description || '';
                
                // Portuguese months mapping to numbers
                const monthMap = {
                  'janeiro': 0, 'fevereiro': 1, 'março': 2, 'abril': 3,
                  'maio': 4, 'junho': 5, 'julho': 6, 'agosto': 7,
                  'setembro': 8, 'outubro': 9, 'novembro': 10, 'dezembro': 11
                };
                
                // Try to extract month and year from description
                const extractMonthYear = (text) => {
                  const lowerText = text.toLowerCase();
                  for (const [monthPt, monthNum] of Object.entries(monthMap)) {
                    if (lowerText.includes(monthPt)) {
                      // Extract year if present
                      const yearMatch = text.match(/\d{4}/);
                      const year = yearMatch ? parseInt(yearMatch[0]) : new Date().getFullYear();
                      // Create localized date string
                      const date = new Date(year, monthNum, 15);
                      return date.toLocaleDateString(t('common.locale') || 'pt-PT', { month: 'long', year: 'numeric' });
                    }
                  }
                  return null;
                };

                // Check for accumulated returns
                if (desc.includes('acumulados') || desc.includes('Accumulated') || desc.includes('até')) {
                  const monthYear = extractMonthYear(desc);
                  if (monthYear) {
                    return t('returns.accumulatedUntil') + ' ' + monthYear;
                  }
                  return t('returns.accumulatedReturns');
                }
                
                // Check for reinvestment
                if (isReinvestment || desc.toLowerCase().includes('reinvest')) {
                  const monthYear = extractMonthYear(desc);
                  if (monthYear) {
                    return t('returns.reinvestmentOf') + ' ' + monthYear;
                  }
                  return t('returns.reinvestment');
                }
                
                // Regular return - try to extract and translate month
                const monthYear = extractMonthYear(desc);
                if (monthYear) {
                  return t('returns.returnOf') + ' ' + monthYear;
                }
                
                return desc;
              };

              return (
                <div key={rendimento.id} className="p-4 hover:bg-white/5 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl ${iconBg} flex items-center justify-center`}>
                        <IconComponent className={`w-6 h-6 ${iconColor}`} />
                      </div>
                      <div>
                        <p className="text-white font-medium">{getTranslatedDescription()}</p>
                        <div className="flex items-center gap-2">
                          <p className="text-sm text-slate-400 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(rendimento.created_at).toLocaleDateString(
                              t('common.locale') || 'pt-PT', 
                              { day: '2-digit', month: 'long', year: 'numeric' }
                            )}
                          </p>
                          {isReinvestment && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400">
                              {t('returns.reinvested')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <span className={`text-lg font-semibold ${amountColor}`}>
                      +{formatCurrency(rendimento.amount)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="glass-card p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
            <TrendingUp className="w-8 h-8 text-slate-500" />
          </div>
          <h3 className="text-lg font-semibold text-white mb-2">{t('returns.noReturns')}</h3>
          <p className="text-slate-400">{t('returns.noReturnsYet')}</p>
        </div>
      )}
    </div>
  );
}
